import { useState } from 'react';
import { useAuth } from '@clerk/react';
import { ActionIcon } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconHeart, IconHeartFilled } from '@tabler/icons-react';
import { useApiClient } from '../../api';

// Heart toggle for favoriting a seller (contract §4.9).
//
// - Renders nothing when signed out (is_favorited is null for anonymous callers).
// - `initialFavorited` seeds internal state on mount (from the seller payload's
//   is_favorited field); after that the component controls itself.
// - Optimistic: flips immediately, reverts + notifies on failure.
// - `onToggled(nowFavorited)` fires after the server confirmed the change
//   (FavoritesPage uses it to refetch its list).
const FavoriteButton = ({ sellerId, initialFavorited = false, onToggled, size = 'lg' }) => {
  const { isSignedIn } = useAuth();
  const api = useApiClient();
  const [favorited, setFavorited] = useState(!!initialFavorited);
  const [busy, setBusy] = useState(false);

  if (!isSignedIn) return null;

  const handleClick = async (e) => {
    // Cards navigate on click — keep the heart from triggering that.
    e.stopPropagation();
    e.preventDefault();
    if (busy) return;

    const next = !favorited;
    setFavorited(next); // optimistic
    setBusy(true);
    try {
      if (next) {
        await api.put(`/api/v1/me/favorites/${sellerId}`);
      } else {
        await api.del(`/api/v1/me/favorites/${sellerId}`);
      }
      onToggled?.(next);
    } catch (err) {
      setFavorited(!next); // revert
      notifications.show({
        title: 'Error',
        message: err?.message || 'Could not update favorites',
        color: 'red',
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <ActionIcon
      variant="subtle"
      color="pink"
      size={size}
      radius="xl"
      onClick={handleClick}
      aria-label={favorited ? 'Remove from favorites' : 'Add to favorites'}
      aria-pressed={favorited}
      title={favorited ? 'Remove from favorites' : 'Add to favorites'}
    >
      {favorited ? <IconHeartFilled size="1.25rem" /> : <IconHeart size="1.25rem" />}
    </ActionIcon>
  );
};

export default FavoriteButton;
